import "@tento-ui/init";
import "@tento-ui/components/breadcrumb/breadcrumb";
import "@tento-ui/components/documentation/documentation";
import "@tento-ui/brand/header/marketing-header";
import "../../lib/marketing/lona-marketing-footer";

import { Breadcrumb } from "@tento-ui/components/breadcrumb/breadcrumb";
import { Documentation } from "@tento-ui/components/documentation/documentation";
import { $ } from "@tento-ui/dom-selectors";
import { DomUtils } from "@tento-ui/dom";
import { DarkMode } from "@tento-ui/dark-mode";

DarkMode.toggle(false, false);

const $breadcrumb = $<Breadcrumb>("breadcrumb");
$breadcrumb.append(
  Breadcrumb.Link("Lona", "/"),
  Breadcrumb.Label("Privacy Policy"),
);

const privacyPolicy = `
Effective date: 2026-06-30

## I. Introduction
Lona Inc. ("Lona", "us", "we", or "our") operates the lona.so website and the Lona application (together, the "Service").

This Privacy Policy explains how we collect, use, disclose, and protect information when you use the Service. If you receive access to the Service through an employer, team, school, or other organization, we may process your information on behalf of that organization under a written agreement with that organization.

We use information to provide, secure, debug, support, and improve the Service. Unless otherwise defined in this Privacy Policy, terms used here have the same meanings as in our Terms of Service.

Definitions

- SERVICE means the lona.so website and Lona application operated by Lona Inc.
- PERSONAL DATA or PERSONAL INFORMATION means data that identifies, relates to, describes, can reasonably be associated with, or could reasonably be linked with a person or household.
- USAGE DATA is data collected automatically either generated by the use of Service or from Service infrastructure itself (for example, the duration of a page visit).
- COOKIES are small files stored on your device (computer or mobile device).
- DATA CONTROLLER means a natural or legal person who determines the purposes and means of processing Personal Data. For personal accounts, Lona is generally the Data Controller. For organization-provided accounts, Lona may process data on behalf of the organization.
- DATA PROCESSORS (OR SERVICE PROVIDERS) means any natural or legal person who processes the data on behalf of the Data Controller. We may use the services of various Service Providers in order to process your data more effectively.
- DATA SUBJECT is any living individual who is the subject of Personal Data.
- THE USER is the individual using our Service. The User corresponds to the Data Subject, who is the subject of Personal Data.

## II. Information Collection and Use
We collect several different types of information for various purposes to provide and improve our Service to you.

### Types of Data Collected

#### Personal Data

While using our Service, we may collect Personal Data that you provide, generate, connect, or authorize us to process. This may include:

- Account information, such as email address, name, authentication details, subscription status, and settings.
- User Content, such as sheets, rows, calendar entries, tasks, notes, stickers, files, chat prompts, AI outputs, and other content you create, import, upload, or share in the Service.
- Calendar data, such as events, schedules, availability, attendees, and related metadata when you connect a third-party calendar such as Google Calendar.
- Fitness, wellness, and health-related data, such as sleep, stress, recovery, activity, readiness, and related metadata when you connect services such as Garmin, Whoop, or Oura.
- Weather, location, travel, and other context you add or request through the Service.
- Connected-account information, such as provider identifiers, OAuth tokens, API keys you add, scopes, and sync metadata.
- Payment and billing information processed through our payment providers. We do not store full payment card numbers.
- Support, feedback, and communications you send to us.
- Usage Data, device data, cookies, logs, crash reports, and diagnostic data, such as IP address, browser type, pages or features used, timestamps, identifiers, errors, and performance information.

We may contact you with transactional, administrative, support, or security messages. Where permitted by law, we may also send marketing communications. You may opt out of marketing communications by following the unsubscribe link or by emailing james@lona.so.

### Tracking Cookies Data

We use cookies and similar tracking technologies to track the activity on our Service and we hold certain information.

Cookies are files with a small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Other tracking technologies are also used such as beacons, tags and scripts to collect and track information and to improve and analyze our Service.

You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.

Examples of Cookies we use:

- Session Cookies:
  - We use Session Cookies to operate our Service.
- Preference Cookies:
  - We use Preference Cookies to remember your preferences and various settings.
- Security Cookies:
  - We use Security Cookies for security purposes.

### Use of Data
Lona Inc. uses collected data for the following purposes:

- to provide, maintain, sync, personalize, and operate the Service;
- to create, display, update, and share User Content according to your settings and instructions;
- to provide connected integrations, including calendar, weather, fitness, health, travel, payment, and AI features you choose to use;
- to generate AI-assisted outputs or automations that you request;
- to provide customer support and respond to your requests;
- to monitor usage, diagnose issues, debug crashes, and improve reliability and performance;
- to use aggregated, anonymized, or redacted operational data, crash reports, and diagnostics to debug crashes, investigate errors, protect the Service, and improve reliability;
- to detect, prevent, and address fraud, abuse, security incidents, and technical issues;
- to process payments, manage subscriptions, and carry out our contractual obligations;
- to send account, subscription, service, security, and administrative notices;
- to send marketing communications where permitted by law and subject to your opt-out rights;
- to comply with law, enforce our agreements, and protect our rights and the rights of others;
- for any other purpose we describe when you provide the information or with your consent.

We do not use User Content, connected calendar data, connected health or fitness data, files, chat prompts, AI outputs, or other connected third-party data to train AI models unless you opt in or separately direct us to do so.

### Retention of Data
We retain Personal Data only for as long as reasonably necessary for the purposes described in this Privacy Policy, including to provide the Service, maintain security, comply with legal obligations, resolve disputes, enforce agreements, and keep appropriate business records.

If you delete a Personal Workspace or private User Content, that content generally becomes inaccessible immediately and is deleted from active systems after 30 days, subject to backups, logs, legal obligations, security needs, and legitimate business records. Usage Data, crash reports, and diagnostics are generally retained for a shorter period, except where needed for security, debugging, product reliability, or legal compliance.

### Transfer of Data
Your information, including Personal Data, may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ from those of your jurisdiction.

If you are located outside United States and choose to provide information to us, please note that we transfer the data, including Personal Data, to United States and process it there.

Lona Inc. will take reasonable steps to ensure that your data is treated securely and in accordance with this Privacy Policy, and that transfers of Personal Data use appropriate safeguards where required.

### Disclosure of Data
We may disclose personal information that we collect, or you provide:

**Disclosure for Law Enforcement.**
Under certain circumstances, we may be required to disclose your Personal Data if required to do so by law or in response to valid requests by public authorities.

**Business Transaction.**
If we or our subsidiaries are involved in a merger, acquisition or asset sale, your Personal Data may be transferred.

**Other cases.** We may disclose your information:
- to service providers, subprocessors, contractors, and other third parties that support the Service;
- to third-party integrations and AI providers when you connect, configure, or use those features;
- to people, guests, teams, organizations, or public surfaces you choose to share with;
- to organization administrators where your account or workspace is controlled by an Organization;
- to fulfill the purpose for which you provided the information;
- to protect the rights, property, or safety of Lona, our users, or others;
- for any other purpose disclosed when you provide the information or with your consent.

We do not sell Personal Data for money. We do not share Personal Data for cross-context behavioral advertising. If this changes, we will update this Privacy Policy and provide any legally required choices.

### Security of Data
The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.

### Your Privacy Rights

Depending on where you live, including if you are in the European Union, European Economic Area, United Kingdom, California, or another jurisdiction with privacy rights, you may have rights over your Personal Data.

We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.

If you wish to access, correct, export, delete, object to, restrict, or limit the use of your Personal Data, please email us at james@lona.so.

In certain circumstances, you have the following data protection rights:

- the right to access, update or to delete the information we have on you;
- the right of rectification. You have the right to have your information rectified if that information is inaccurate or incomplete;
- the right to object. You have the right to object to our processing of your Personal Data;
- the right of restriction. You have the right to request that we restrict the processing of your personal information;
- the right to data portability. You have the right to be provided with a copy of your Personal Data in a structured, machine-readable and commonly used format;
- the right to withdraw consent. You also have the right to withdraw your consent at any time where we rely on your consent to process your personal information;

Please note that we may ask you to verify your identity before responding to such requests. We may not be able to provide the Service without some necessary data.

You may have the right to complain to a data protection authority about our collection and use of your Personal Data.

### California Online Privacy Notice

This Privacy Policy is linked from Lona surfaces with the word "Privacy". We post updates on this page and update the effective date when this Privacy Policy changes. You may request access to or correction of your personal information by emailing james@lona.so.


### Our Policy on "Do Not Track" Signals:

Some browsers offer "Do Not Track" or opt-out preference signals. We do not sell Personal Data or share Personal Data for cross-context behavioral advertising. If we begin using data in a way that requires honoring browser opt-out preference signals, we will honor legally required signals and update this Privacy Policy.

### California Privacy Rights
If you are a California resident, you may be entitled to learn what data we collect about you, ask us to delete or correct your data, opt out of sale or sharing of personal information, limit certain uses of sensitive personal information, and exercise related privacy rights. To exercise your data protection rights, you can make certain requests and ask us:

What personal information we have about you. If you make this request, we will return to you:

- The categories of personal information we have collected about you.
- The categories of sources from which we collect your personal information.
- The business or commercial purpose for collecting, using, disclosing, selling, or sharing your personal information.
- The categories of third parties with whom we share personal information.
- The specific pieces of personal information we have collected about you.
- A list of categories of personal information that we have sold or shared, along with the category of any third party we sold or shared it with. If we have not sold or shared your personal information, we will inform you of that fact.
- A list of categories of personal information that we have disclosed for a business purpose, along with the category of any other company we shared it with.

Please note, you are entitled to ask us to provide you with this information up to two times in a rolling twelve-month period. When you make this request, the information provided may be limited to the personal information we collected about you in the previous 12 months.

**To delete your personal information.** If you make this request, we will delete the personal information we hold about you as of the date of your request from our records and direct any service providers to do the same. In some cases, deletion may be accomplished through de-identification of the information. If you choose to delete your personal information, you may not be able to use certain functions that require your personal information to operate.

**To correct inaccurate personal information.** If you make this request, we will take reasonable steps to correct inaccurate personal information we maintain about you.

**To limit use of sensitive personal information.** Some connected calendar, health, fitness, and location-related data may be considered sensitive personal information under applicable law. We use sensitive personal information only as reasonably necessary to provide the Service, maintain security, debug issues, comply with law, or for purposes you authorize. You may request to limit use of sensitive personal information by emailing james@lona.so, but some Service features may not work without the data they require.

**To opt out of sale or sharing.** We do not sell Personal Data for money. We do not share Personal Data for cross-context behavioral advertising.

- You may exercise California privacy rights by emailing james@lona.so. You may use a subject line such as "Privacy Rights Request" or "Do Not Sell or Share My Personal Information".
Please note, if you ask us to delete, restrict, or limit your data, it may impact your experience with us, and you may not be able to use features that require the data to function. We will not discriminate against you for exercising your rights.

**To exercise your California data protection rights described above.** Please send your request(s) by email: james@lona.so

Your California privacy rights may be covered by the California Consumer Privacy Act, as amended by the California Privacy Rights Act, and related regulations.

## III. Service Providers

We may employ third-party companies and individuals to facilitate our Service ("Service Providers"), provide the Service on our behalf, perform Service-related services, process payments, host infrastructure, support integrations, provide AI features you request, or assist us in analyzing how our Service is used.

These third parties have access to your Personal Data only to perform these tasks on our behalf, under their terms and our agreements with them where applicable. They are not authorized by Lona to use your Personal Data for their own unrelated purposes.

### Analytics
We may use third-party Service Providers to monitor and analyze the use of our Service.

**Google Analytics**

Google Analytics is a web analytics service offered by Google that helps us understand website traffic and product usage. We configure analytics tools to support operational analytics and do not use them to sell Personal Data or share Personal Data for cross-context behavioral advertising.

For more information on the privacy practices of Google, please visit the Google Privacy Terms web page: https://policies.google.com/privacy?hl=en
We also encourage you to review the Google's policy for safeguarding your data: https://support.google.com/analytics/answer/6004245

### CI/CD tools
We may use third-party Service Providers to automate the development process of our Service.

**GitHub**

GitHub is provided by GitHub, Inc.

GitHub is a development platform to host and review code, manage projects, and build software.

For more information on what data GitHub collects for what purpose and how the protection of the data is ensured, please visit GitHub Privacy Policy page: https://help.github.com/en/articles/github-privacy-statement

### Payments
We may provide paid products and/or services within Service. In that case, we use third-party services for payment processing (e.g. payment processors).

We will not store or collect your payment card details. That information is provided directly to our third-party payment processors whose use of your personal information is governed by their Privacy Policy. These payment processors adhere to the standards set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint effort of brands like Visa, Mastercard, American Express and Discover. PCI-DSS requirements help ensure the secure handling of payment information.

The payment processors we work with are:

**Stripe:**

Their Privacy Policy can be viewed at: https://stripe.com/us/privacy

### Calendar Integration
We integrate with third-party calendar services to provide calendar synchronization features.

**Google Calendar**

When you connect your Google account, we access your Google Calendar data (events, schedules, and related metadata) to display and synchronize it within our Service. We request only the permissions necessary to read and write calendar data on your behalf. You can disconnect your Google account at any time from your account settings.

For more information, please visit the Google Privacy Policy: https://policies.google.com/privacy?hl=en

### Fitness and Health Integrations
We integrate with third-party fitness and health services to display wellness data within our Service.

**Whoop**

When you connect your Whoop account, we access sleep and recovery data to display within the Service. For more information, please visit the Whoop Privacy Policy: https://www.whoop.com/privacy/

**Garmin**

When you connect your Garmin account, we access stress and activity data to display within the Service. For more information, please visit the Garmin Privacy Policy: https://www.garmin.com/en-US/privacy/connect/

**Oura**

When you connect your Oura account, we may access sleep, readiness, activity, and related data to display within the Service. For more information, please visit the Oura Privacy Policy: https://ouraring.com/privacy-policy

### AI Features
We may use third-party artificial intelligence services, including Google Gemini, Claude, OpenAI, OpenAI-compatible providers, and other AI providers, to provide AI-powered features within the Service. Some features may use API keys you provide.

**AI Providers**

When you use AI-assisted features, we may send relevant prompts, context, User Content, connected data, tool results, and outputs to the applicable AI provider as needed to provide the feature you requested. We do not use User Content, connected calendar data, connected health or fitness data, files, chat prompts, AI outputs, or other connected third-party data to train AI models unless you opt in or separately direct us to do so. We may use aggregated, anonymized, or redacted operational data, crash reports, and diagnostics to debug crashes, investigate errors, protect the Service, and improve reliability.

For more information, please review the applicable provider's terms and privacy policy, such as Google Gemini API terms at https://ai.google.dev/terms

### Infrastructure
We use third-party infrastructure providers to host and deliver our Service.

**Cloudflare**

We use Cloudflare for DNS management and content delivery. Cloudflare may process network traffic data (such as IP addresses) as part of delivering our Service.

For more information, please visit: https://www.cloudflare.com/privacypolicy/

**Amazon Web Services (AWS)**

We use AWS to host our servers and store data. For more information, please visit: https://aws.amazon.com/privacy/

### Links to Other Sites

Our Service may contain links to other sites that are not operated by us. If you click a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.

## IV. Children's Privacy
Our Services are not intended for use by children under the age of 13 ("Child" or "Children").

We do not knowingly collect personally identifiable information from children under 13. If you become aware that a child has provided us with Personal Data, please contact us at james@lona.so. If we become aware that we have collected Personal Data from a child without verification of parental consent, we take steps to remove that information from our servers.

## V. Changes to This Privacy Policy

We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.

For material changes, we may provide additional notice, such as by email or a prominent notice in the Service, when required by law or when we determine it is appropriate. We will update the effective date at the top of this Privacy Policy when it changes.

You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.

## VI. Contact Us
If you have any questions about this Privacy Policy, please contact us by email: james@lona.so
`;

const $privacyPolicy = $<Documentation>("privacy-policy");
await $privacyPolicy.bind({ markdown: privacyPolicy, nodeViews: {} });

await DomUtils.hydrateAndReveal();
